import { Bus, type SoundBusManager } from '@src/audio/SoundBusManager';
import { Config } from '@src/core/Config';

const SFX_PATH = '/assets/sfx';

/**
 * ElevenLabs boss-death stems under public/assets/sfx/. Magus reuses the lich
 * spectral implosion; Sentinel reuses the colossus stone collapse.
 */
export const BOSS_DEATH_SAMPLE_IDS = [
  'boss_death_lich',
  'boss_death_warlord',
  'boss_death_colossus',
  'boss_death_matriarch',
  'boss_death_druid',
  'boss_death_skullking',
] as const;

export type BossDeathSampleId = (typeof BOSS_DEATH_SAMPLE_IDS)[number];

/**
 * ElevenLabs loot + attack one-shots under public/assets/sfx/. Procedural
 * synth in `play()` remains the fallback when a file is missing.
 */
export const ACTION_SFX_SAMPLE_IDS = [
  'loot_gem',
  'loot_prize',
  'loot_heart',
  'loot_weapon',
  'attack_wand',
  'attack_fire',
  'attack_cleaver',
  'attack_crossbow',
  'attack_phase',
  'attack_hit',
  'boss_hit',
  'attack_explosion',
] as const;

export type ActionSfxSampleId = (typeof ACTION_SFX_SAMPLE_IDS)[number];

/** ElevenLabs stadium crowd stems. CrowdBed is the gameplay mixer; these back CrowdRoar / CrowdApplause. */
export const CROWD_SFX_SAMPLE_IDS = [
  'crowd_cheer',
  'crowd_cheer_big',
  'crowd_gasp',
  'crowd_boo',
  'crowd_applause',
  'crowd_murmur',
  'crowd_ooh',
] as const;

export type CrowdSfxSampleId = (typeof CROWD_SFX_SAMPLE_IDS)[number];

/** Per-sample gain. The wand stem is mastered hot and used as the starter gun. */
const SAMPLE_GAIN: Partial<Record<string, number>> = {
  attack_wand: 0.34,
};

/** Cap how long a one-shot rings so rapid fire cannot stack into a wall. */
const SAMPLE_MAX_SEC: Partial<Record<string, number>> = {
  attack_wand: 0.12,
};

export enum SfxId {
  ShotWand = 0,
  ShotCleaver,
  ShotCrossbow,
  Hit,
  Kill,
  GolemKill,
  PortalHit,
  PortalDown,
  PickupWeapon,
  Overcharge,
  Gem,
  Prize,
  PlayerHurt,
  GateSlam,
  Shockwave,
  DragonArrive,
  DragonRoar,
  DragonBurn,
  Streak,
  RoomClear,
  GateOpen,
  Heal,
  SupplyDrop,
  /** Kill streak chime, second tier (40+ combo). */
  Streak2,
  /** Kill streak chime, top tier (80+ combo). */
  Streak3,
  PlayerDeath,
  /** New small-fry pack debuts: short ominous sting. */
  NewThreat,
  WeaponExpired,
  /** Stall / corner-camping klaxon before the wyrm. */
  Alarm,
  /** Immediate warning ping when player enters low power state. */
  LowPowerWarning,
  /** Rhythmic heartbeat thud while surviving at low power. */
  Heartbeat,
  MinimapZoom,
  /** Opening curtain whoosh under "GATES ARE LOCKED". */
  Curtain,
  /** Opening cinematic: studio crowd roar (intensity scales length and size). */
  CrowdRoar,
  /** Opening cinematic: applause, a few hundred hands. */
  CrowdApplause,
  /** Opening cinematic: accelerating snare roll ending on a hit. */
  Drumroll,
  /** Opening cinematic: brass and choir stab with a sub boom; title card. */
  OrchestraHit,
  /** Opening cinematic: "on air" sigil lighting up. */
  OnAir,
  /** Commercial break: upbeat broadcast sponsor jingle. */
  CommercialJingle,
  /** Opening cinematic: rune wipe between panels. */
  RuneWipe,
  /** Floor boss entrance: sub drop, rune-circle shimmer, choir-ish swell. */
  BossArrive,
  /** Boss telegraph: rising charge-up shimmer before a release. */
  BossTelegraph,
  /** Boss release: arcane thump plus a spray of glassy partials. */
  BossCast,
  /** Boss takes a hit: dull crack with a grunt-like formant. */
  BossHurt,
  /** Boss at half health: cracked bell over a growl. */
  BossHalf,
  /** Boss blinks between rune points. */
  BossBlink,
  /** Boss death: long descending crescendo and final blast. */
  BossDeath,
  /** Start screen: rune cursor moves between difficulty cards. */
  MenuMove,
  /** Start screen: difficulty locked in. */
  MenuConfirm,
  /** Last portal down: one-second rune build (sub rise, tightening shaker, glints) ending on a pre-hit. */
  BossIncoming,
  /** Warlord entrance: sub-bass drop, stone grind, war horn. */
  BossArriveWarlord,
  /** Skull King entrance: reversed cymbal, choir shriek, bone rattle, vacuum pop. */
  BossArriveSkullKing,
  /** Colossus entrance: wet suction, bubbling low gurgle, heave. */
  BossArriveColossus,
  /** Matriarch entrance: screech sweep over wing-flap flutter. */
  BossArriveMatriarch,
  /** Sentinel entrance: stone cracks, charging hum, eye ignition. */
  BossArriveSentinel,
  /** Telegraph: rising tone (aimed fan / rubble volley). */
  BossTelegraphAimed,
  /** Telegraph: low pulse (radial rings, ring-gap). */
  BossTelegraphRadial,
  /** Telegraph: glassy shimmer (orbit, crown, wisps, summon). */
  BossTelegraphOrb,
  /** Telegraph: sawtooth charge (beam sweep). */
  BossTelegraphBeam,
  /** Telegraph: stone wind-up grind (stomp, charge). */
  BossTelegraphHeavy,
  /** Telegraph: sharp inhale (screech, dive). */
  BossTelegraphInhale,
  /** Telegraph: gurgling swell (acid). */
  BossTelegraphGurgle,
  /** Hurt take: cracking stone. */
  BossHurtStone,
  /** Hurt take: hollow bone knock and rattle. */
  BossHurtBone,
  /** Hurt take: wet goo splat. */
  BossHurtGoo,
  /** Hurt take: leather slap and a short screech. */
  BossHurtLeather,
  /** Heavy ground impact: stomp / crash landing. */
  BossStomp,
  /** Charge slams into the wall: stone smash and rubble. */
  BossWallHit,
  /** Screech shockwave: piercing sweep and an air blast. */
  BossScreech,
  /** Acid glob bursts on the floor. */
  BossSplat,
  /** Minions called: chant swell with a pop per body. */
  BossSummon,
  /** Colossus split: giant wet tear. */
  BossSplit,
  /** Skull launched from the crown: bone whistle. */
  BossSkullLaunch,
  /** Death crescendo, stone body (Warlord, Sentinel): rockfall, dying horn, blast, bell. */
  BossDeathStone,
  /** Death crescendo, bone body: rattle storm, rising shriek, blast, choir tail. */
  BossDeathBone,
  /** Death crescendo, goo body: inflating gurgle, pop, splat rain. */
  BossDeathGoo,
  /** Death crescendo, leather body: frantic flaps, screech, blast, swarm dispersal. */
  BossDeathLeather,
  /** One-bar drum fill that hands the floor track over to the boss track. */
  BossDrumFill,
  /** Boss down: single hit then a beat of silence before the room-clear sting. */
  BossDownHit,
  /** Fireball / mortar launch (Hydra Mortar, sun-fire cues). */
  ShotFire,
  /** Phase-bolt launch (Phase Crossbow dimensional zap). */
  ShotPhase,
  /** Rune heart grab — same cue family as Heal; kept for sample mapping clarity. */
  SupplyHeart,
  /** Concussive blast: mortar impact, shockwave bloom, debris scatter. */
  Explosion,
  /** Player-death broadcast: long orchestral slam under the elimination replay. */
  DeathSting,
  /** Floor mine fuse click. */
  MineArmed,
  /** Cataclysm Orb: room-wide detonation. */
  Cataclysm,
  /** Rune Ward pickup. */
  PickupShield,
  /** Gale Step pickup. */
  PickupSwift,
  /** Ward blade hit (throttled). */
  OffhandHit,
  /** Victory broadcast: bright orchestral slam under the championship replay. */
  VictorySting,
  /** Player double-tap dash. */
  Dash,
}

/** Room for boss / future ids without touching the rate-limit tables. */
const SFX_SLOTS = 128;

/**
 * Fully procedural one-shot effects built from oscillators and a pre-rendered
 * noise buffer. Rate limited per id so 300 imps dying at once do not clip.
 */
export class Sfx {
  private readonly noise: AudioBuffer;
  private readonly lastPlayed = new Float64Array(SFX_SLOTS);
  private readonly minGap = new Float64Array(SFX_SLOTS).fill(0.03);
  private readonly lastTake = new Uint8Array(SFX_SLOTS);
  private readonly out: GainNode;
  /** Current generation of one-shots. `stopVoices()` fades this and opens a fresh bus. */
  private voices: GainNode;
  private voiceGen = 0;
  private beamNode: { osc: OscillatorNode; gain: GainNode; lfo: OscillatorNode } | null = null;
  /** Per-play randomization applied inside blip/burst/arp: pitch, duration, filter cutoff. */
  private pitchMul = 1;
  private stretchMul = 1;
  private toneMul = 1;
  /** Decoded one-shot samples (boss deaths, etc.). Missing files cache as null. */
  private readonly samples = new Map<string, AudioBuffer | null>();
  private readonly sampleLoading = new Map<string, Promise<AudioBuffer | null>>();

  constructor(private readonly buses: SoundBusManager) {
    const ctx = buses.context;
    this.noise = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.out = ctx.createGain();
    this.out.gain.value = 1;
    this.out.connect(buses.bus(Bus.Sfx));
    this.voices = ctx.createGain();
    this.voices.gain.value = 1;
    this.voices.connect(this.out);
    // Rapid-fire shots: short gaps so stacks stay punchy without muddying.
    this.minGap[SfxId.ShotWand] = 0.11;
    this.minGap[SfxId.ShotCleaver] = 0.1;
    this.minGap[SfxId.ShotCrossbow] = 0.07;
    this.minGap[SfxId.ShotFire] = 0.12;
    this.minGap[SfxId.ShotPhase] = 0.07;
    this.minGap[SfxId.Hit] = 0.07;
    this.minGap[SfxId.Explosion] = 0.22;
    this.minGap[SfxId.Kill] = 0.1;
    this.minGap[SfxId.PortalHit] = 0.2;
    this.minGap[SfxId.Gem] = 0.1;
    this.minGap[SfxId.PickupWeapon] = 0.35;
    this.minGap[SfxId.SupplyHeart] = 0.3;
    this.minGap[SfxId.RoomClear] = 0.7;
    this.minGap[SfxId.GateOpen] = 1.0;
    this.minGap[SfxId.Prize] = 0.3;
    this.minGap[SfxId.NewThreat] = 1.0;
    this.minGap[SfxId.Alarm] = 1.5;
    this.minGap[SfxId.LowPowerWarning] = 0.8;
    this.minGap[SfxId.Heartbeat] = 0.35;
    this.minGap[SfxId.MinimapZoom] = 0.4;
    this.minGap[SfxId.CrowdRoar] = 0.8;
    this.minGap[SfxId.CrowdApplause] = 1.2;
    this.minGap[SfxId.CommercialJingle] = 1.0;
    this.minGap[SfxId.PlayerDeath] = 2.0;
    this.minGap[SfxId.BossArrive] = 3.0;
    this.minGap[SfxId.BossTelegraph] = 0.3;
    this.minGap[SfxId.BossCast] = 0.08;
    this.minGap[SfxId.BossHurt] = 0.2;
    this.minGap[SfxId.BossHalf] = 3.0;
    this.minGap[SfxId.BossBlink] = 0.5;
    this.minGap[SfxId.BossDeath] = 4.0;
    this.minGap[SfxId.BossIncoming] = 2.0;
    for (let id = SfxId.BossArriveWarlord; id <= SfxId.BossArriveSentinel; id++) this.minGap[id] = 3.0;
    for (let id = SfxId.BossTelegraphAimed; id <= SfxId.BossTelegraphGurgle; id++) this.minGap[id] = 0.25;
    for (let id = SfxId.BossHurtStone; id <= SfxId.BossHurtLeather; id++) this.minGap[id] = 0.2;
    this.minGap[SfxId.BossStomp] = 0.3;
    this.minGap[SfxId.BossWallHit] = 0.5;
    this.minGap[SfxId.BossScreech] = 0.5;
    this.minGap[SfxId.BossSplat] = 0.12;
    this.minGap[SfxId.BossSummon] = 0.8;
    this.minGap[SfxId.BossSplit] = 1.5;
    this.minGap[SfxId.BossSkullLaunch] = 0.08;
    for (let id = SfxId.BossDeathStone; id <= SfxId.BossDeathLeather; id++) this.minGap[id] = 4.0;
    this.minGap[SfxId.BossDrumFill] = 3.0;
    this.minGap[SfxId.BossDownHit] = 3.0;
    this.minGap[SfxId.DeathSting] = 4.0;
    this.minGap[SfxId.MineArmed] = 0.08;
    this.minGap[SfxId.Cataclysm] = 0.8;
    this.minGap[SfxId.PickupShield] = 0.35;
    this.minGap[SfxId.PickupSwift] = 0.3;
    this.minGap[SfxId.OffhandHit] = 0.16;
    this.minGap[SfxId.VictorySting] = 4.0;
    this.minGap[SfxId.Dash] = 0.18;
  }

  /** Warm decoded sample cache so the first boss kill does not hitch. */
  preload(ids: Iterable<string>): void {
    for (const id of ids) void this.loadSample(id);
  }

  /** True once a sample is decoded and playable. */
  hasSample(id: string): boolean {
    return !!this.samples.get(id);
  }

  /**
   * Play a preloaded one-shot from `/assets/sfx/<id>.mp3`. Returns false when
   * the file is missing or still loading so callers can fall back to procedural.
   * `pitchVar` is fractional playback-rate jitter (loot cues use a bit more).
   */
  playSample(id: string, intensity = 1, delaySec = 0, pitchVar = 0.03): boolean {
    const buffer = this.samples.get(id);
    if (!buffer) return false;
    const t = this.buses.now + delaySec;
    const ctx = this.buses.context;
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    // Pitch variance so rapid repeats do not feel identical.
    src.playbackRate.value = this.vary(1, pitchVar);
    const g = ctx.createGain();
    const vol = Math.min(1.35, Math.max(0.05, intensity * (SAMPLE_GAIN[id] ?? 1)));
    const capped = SAMPLE_MAX_SEC[id];
    const dur = capped ? Math.min(buffer.duration, capped) : buffer.duration;
    // Fast attacks stay snappy; longer stems fade on their natural tail.
    const attack = Math.min(capped ? 0.012 : 0.02, dur * 0.08);
    const fade = capped ? Math.min(0.05, dur * 0.35) : Math.min(0.25, dur * 0.35);
    const holdEnd = Math.max(attack + 0.02, dur - fade);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + attack);
    g.gain.setValueAtTime(vol, t + holdEnd);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.02);
    src.connect(g);
    g.connect(this.voices);
    src.start(t);
    src.stop(t + dur + 0.04);
    return true;
  }

  /**
   * Soft sub rumble under a cinematic boss-death sample so the room still
   * shakes when the MP3 carries the character of the kill.
   */
  playDeathRumble(intensity = 0.7, delaySec = 0): void {
    const t = this.buses.now + delaySec;
    this.burst(t, 2.4, 0.14 * intensity, 140, 18);
    this.blip(t, 52, 28, 2.6, 0.38 * intensity, 'sine');
    this.blip(t, 78, 36, 2.2, 0.16 * intensity, 'sine');
  }

  /**
   * Fade every in-flight voice (including delayed nodes already connected) and
   * the beam loop. Plays after this ride a new generation, so a death sting or
   * room-clear arp is not killed with the leftover shots.
   */
  stopVoices(fade = 0.15): void {
    const ctx = this.buses.context;
    const t = ctx.currentTime;
    const kill = Math.max(0.04, fade);
    const old = this.voices;
    this.voiceGen++;
    this.voices = ctx.createGain();
    this.voices.gain.value = 1;
    this.voices.connect(this.out);
    old.gain.cancelScheduledValues(t);
    old.gain.setValueAtTime(Math.max(0.0001, old.gain.value), t);
    old.gain.exponentialRampToValueAtTime(0.0001, t + kill);
    window.setTimeout(() => {
      try {
        old.disconnect();
      } catch {
        // already gone
      }
    }, Math.ceil((kill + 0.2) * 1000));
    this.setBeam(false, false);
  }

  /** Alias for `stopVoices`: combat beds and one-shot tails from the last phase. */
  stopCombat(fade = 0.16): void {
    this.stopVoices(fade);
  }

  play(id: SfxId, intensity = 1, delaySec = 0): void {
    const now = this.buses.now;
    if (now - this.lastPlayed[id] < this.minGap[id]) return;
    this.lastPlayed[id] = now;
    const t = now + delaySec;
    // Every play gets its own pitch (+-8%), length (+-15%) and brightness (+-20%).
    this.pitchMul = this.vary(1, 0.08);
    this.stretchMul = this.vary(1, 0.15);
    this.toneMul = this.vary(1, 0.2);
    switch (id) {
      case SfxId.ShotWand:
        if (this.playSample('attack_wand', intensity, delaySec, 0.04)) break;
        this.shotWand(t, intensity);
        break;
      case SfxId.ShotFire:
        if (this.playSample('attack_fire', intensity, delaySec, 0.035)) break;
        this.burst(t, 0.28, 0.35 * intensity, 1400, 3);
        this.blip(t, 180, 60, 0.22, 0.28 * intensity, 'sawtooth');
        this.whoosh(t, 0.35, 0.12 * intensity, 400, 2800);
        break;
      case SfxId.ShotCleaver:
        if (this.playSample('attack_cleaver', intensity, delaySec, 0.04)) break;
        if (this.pickTake(SfxId.ShotCleaver, 2) === 0) {
          this.burst(t, 0.22, 0.32 * intensity, 900, 4);
          this.blip(t, 220, 90, 0.2, 0.25 * intensity, 'sawtooth');
        } else {
          // Heavier swing: darker whoosh, square growl.
          this.burst(t, 0.26, 0.3 * intensity, 700, 5);
          this.blip(t, 180, 70, 0.24, 0.24 * intensity, 'square');
        }
        break;
      case SfxId.ShotCrossbow:
        if (this.playSample('attack_crossbow', intensity, delaySec, 0.04)) break;
        if (this.pickTake(SfxId.ShotCrossbow, 2) === 0) {
          this.blip(t, 1800, 400, 0.09, 0.16 * intensity, 'sawtooth');
          this.burst(t, 0.06, 0.12 * intensity, 4000, 1);
        } else {
          // Twang: two quick partials, shorter hiss.
          this.blip(t, 2200, 500, 0.06, 0.12 * intensity, 'triangle');
          this.blip(t + 0.02, 1500, 350, 0.08, 0.1 * intensity, 'sawtooth');
          this.burst(t, 0.04, 0.1 * intensity, 5000, 1);
        }
        break;
      case SfxId.ShotPhase:
        if (this.playSample('attack_phase', intensity, delaySec, 0.04)) break;
        this.blip(t, 420, 180, 0.12, 0.2 * intensity, 'sawtooth');
        this.blip(t, 840, 360, 0.1, 0.1 * intensity, 'square');
        this.burst(t, 0.08, 0.14 * intensity, 3200, 2);
        this.whoosh(t, 0.2, 0.08 * intensity, 800, 3600);
        break;
      case SfxId.Hit:
        if (this.playSample('attack_hit', intensity, delaySec, 0.05)) break;
        this.hit(t, intensity);
        break;
      case SfxId.Explosion:
        if (this.playSample('attack_explosion', intensity, delaySec, 0.03)) break;
        this.blip(t, 90, 28, 0.7, 0.7 * intensity, 'sine');
        this.burst(t, 0.55, 0.55 * intensity, 500, 6);
        this.burst(t, 0.18, 0.35 * intensity, 3200, 1);
        this.whoosh(t + 0.05, 0.45, 0.12 * intensity, 600, 2400);
        break;
      case SfxId.MineArmed:
        this.blip(t, 880, 1400, 0.07, 0.16 * intensity, 'square');
        this.blip(t + 0.04, 220, 90, 0.12, 0.12 * intensity, 'sawtooth');
        break;
      case SfxId.Cataclysm:
        if (this.playSample('attack_explosion', intensity, delaySec, 0.03)) {
          this.blip(t, 48, 22, 0.9, 0.7 * intensity, 'sine');
          this.whoosh(t, 0.7, 0.2 * intensity, 200, 1800);
          break;
        }
        this.blip(t, 55, 22, 1.0, 0.85 * intensity, 'sine');
        this.burst(t, 0.75, 0.7 * intensity, 280, 10);
        this.burst(t, 0.22, 0.4 * intensity, 2800, 2);
        this.whoosh(t, 0.7, 0.22 * intensity, 180, 2200);
        this.blip(t + 0.08, 320, 80, 0.45, 0.28 * intensity, 'sawtooth');
        break;
      case SfxId.PickupShield:
        this.arp(t, [392, 523, 659, 784, 1047], 0.055, 0.2 * intensity);
        this.blip(t, 180, 420, 0.35, 0.14 * intensity, 'sine');
        break;
      case SfxId.PickupSwift:
        this.arp(t, [587, 784, 988, 1319], 0.045, 0.2 * intensity);
        this.whoosh(t, 0.28, 0.08 * intensity, 400, 2200);
        break;
      case SfxId.OffhandHit:
        this.blip(t, 720, 240, 0.08, 0.12 * intensity, 'triangle');
        this.burst(t, 0.08, 0.1 * intensity, 1800, 2);
        break;
      case SfxId.Kill:
        this.kill(t, intensity);
        break;
      case SfxId.GolemKill:
        this.golemKill(t, intensity);
        break;
      case SfxId.PortalHit:
        this.portalHit(t, intensity);
        break;
      case SfxId.PortalDown:
        if (this.pickTake(SfxId.PortalDown, 2) === 0) {
          this.blip(t, 90, 28, 0.9, 0.8 * intensity, 'sine');
          this.burst(t, 0.7, 0.55 * intensity, 300, 12);
          this.blip(t + 0.05, 1200, 200, 0.5, 0.2 * intensity, 'sawtooth');
        } else {
          // Implosion: glass shards first, then the thud.
          this.burst(t, 0.12, 0.3 * intensity, 3200, 2);
          this.blip(t + 0.06, 1600, 120, 0.4, 0.18 * intensity, 'square');
          this.blip(t + 0.1, 70, 30, 0.8, 0.75 * intensity, 'sine');
          this.burst(t + 0.1, 0.6, 0.45 * intensity, 260, 10);
        }
        break;
      case SfxId.PickupWeapon:
        if (this.playSample('loot_weapon', intensity, delaySec, 0.045)) break;
        if (this.pickTake(SfxId.PickupWeapon, 2) === 0) this.arp(t, [440, 660, 880, 1320], 0.07, 0.22 * intensity);
        else this.arp(t, [392, 587, 784, 1175, 1568], 0.06, 0.2 * intensity);
        break;
      case SfxId.Overcharge:
        this.arp(t, [330, 440, 660, 880, 1320, 1760], 0.055, 0.28 * intensity);
        this.blip(t, 60, 120, 0.6, 0.5 * intensity, 'sine');
        break;
      case SfxId.Gem: {
        if (this.playSample('loot_gem', intensity, delaySec, 0.055)) break;
        const g = this.pickTake(SfxId.Gem, 3);
        const f0 = 1280 + Math.random() * 520;
        if (g === 0) this.blip(t, f0, 2200, 0.09, 0.1 * intensity, 'sine');
        else if (g === 1) this.blip(t, f0 * 0.85, f0 * 1.4, 0.11, 0.11 * intensity, 'triangle');
        else {
          this.blip(t, f0, f0 * 1.15, 0.06, 0.09 * intensity, 'sine');
          this.blip(t + 0.04, f0 * 1.25, 2400, 0.07, 0.08 * intensity, 'triangle');
        }
        break;
      }
      case SfxId.Prize:
        if (this.playSample('loot_prize', intensity, delaySec, 0.04)) break;
        this.prize(t, intensity);
        break;
      case SfxId.PlayerHurt:
        this.playerHurt(t, intensity);
        break;
      case SfxId.GateSlam:
        if (this.pickTake(SfxId.GateSlam, 2) === 0) {
          this.blip(t, 70, 30, 0.5, 0.7 * intensity, 'sine');
          this.burst(t, 0.25, 0.5 * intensity, 400, 10);
        } else {
          // Iron clang riding the thud.
          this.blip(t, 64, 28, 0.55, 0.68 * intensity, 'sine');
          this.burst(t, 0.2, 0.42 * intensity, 500, 8);
          this.blip(t + 0.01, 620, 540, 0.18, 0.12 * intensity, 'square');
        }
        break;
      case SfxId.Shockwave:
        if (this.pickTake(SfxId.Shockwave, 2) === 0) {
          this.blip(t, 400, 40, 0.4, 0.4 * intensity, 'sawtooth');
          this.burst(t, 0.3, 0.3 * intensity, 1200, 3);
        } else {
          this.blip(t, 300, 35, 0.5, 0.38 * intensity, 'square');
          this.burst(t, 0.36, 0.28 * intensity, 900, 4);
        }
        break;
      case SfxId.DragonArrive:
        // Wingbeats under a rising roar.
        this.blip(t, 55, 110, 1.6, 0.6 * intensity, 'sawtooth');
        this.blip(t, 58, 116, 1.6, 0.6 * intensity, 'sawtooth');
        this.burst(t, 1.2, 0.35 * intensity, 200, 20);
        this.burst(t + 0.4, 0.3, 0.3 * intensity, 350, 6);
        this.burst(t + 0.9, 0.3, 0.3 * intensity, 350, 6);
        break;
      case SfxId.DragonRoar:
        if (this.pickTake(SfxId.DragonRoar, 2) === 0) {
          this.blip(t, 120, 70, 0.6, 0.45 * intensity, 'sawtooth');
          this.blip(t, 121.5, 72, 0.6, 0.45 * intensity, 'square');
          this.burst(t, 0.5, 0.35 * intensity, 900, 5);
        } else {
          // Rising snarl before the breath.
          this.blip(t, 80, 140, 0.55, 0.42 * intensity, 'sawtooth');
          this.blip(t + 0.02, 83, 146, 0.55, 0.4 * intensity, 'square');
          this.burst(t + 0.1, 0.45, 0.32 * intensity, 1100, 4);
        }
        break;
      case SfxId.DragonBurn:
        if (this.pickTake(SfxId.DragonBurn, 2) === 0) {
          this.burst(t, 0.5, 0.45 * intensity, 1800, 2);
          this.blip(t, 240, 50, 0.4, 0.3 * intensity, 'square');
        } else {
          this.burst(t, 0.42, 0.4 * intensity, 2400, 1);
          this.burst(t + 0.08, 0.3, 0.2 * intensity, 1200, 3);
          this.blip(t, 300, 60, 0.35, 0.28 * intensity, 'sawtooth');
        }
        break;
      case SfxId.Streak:
        this.arp(t, [660, 880, 1320, 1760, 2640], 0.045, 0.22 * intensity);
        this.blip(t, 80, 160, 0.35, 0.35 * intensity, 'sine');
        break;
      case SfxId.Streak2:
        this.arp(t, [660, 990, 1320, 1980, 2640, 3960], 0.04, 0.19 * intensity);
        this.blip(t, 70, 180, 0.4, 0.34 * intensity, 'sine');
        this.burst(t, 0.2, 0.1 * intensity, 4000, 1);
        break;
      case SfxId.Streak3:
        this.arp(t, [523, 784, 1047, 1568, 2093, 3136, 4186], 0.035, 0.17 * intensity);
        this.arp(t + 0.05, [659, 988, 1319, 1976], 0.035, 0.1 * intensity);
        this.blip(t, 60, 200, 0.5, 0.34 * intensity, 'sine');
        this.burst(t, 0.3, 0.12 * intensity, 5000, 1);
        break;
      case SfxId.RoomClear:
        if (this.pickTake(SfxId.RoomClear, 2) === 0) this.arp(t, [392, 494, 587, 784, 988], 0.11, 0.3 * intensity);
        else this.arp(t, [392, 587, 494, 784, 1175], 0.1, 0.3 * intensity);
        break;
      case SfxId.GateOpen:
        // Chain ratchet clicks over a low stone rumble, then the bars seat with a clank.
        this.burst(t, 1.0, 0.25 * intensity, 180, 24);
        for (let k = 0; k < 7; k++) this.burst(t + 0.08 + k * 0.13, 0.03, 0.14 * intensity, 2600, 2);
        this.blip(t + 1.05, 900, 300, 0.12, 0.18 * intensity, 'square');
        this.blip(t + 1.05, 110, 60, 0.3, 0.3 * intensity, 'sine');
        break;
      case SfxId.Heal:
      case SfxId.SupplyHeart:
        if (this.playSample('loot_heart', intensity, delaySec, 0.045)) break;
        if (this.pickTake(SfxId.Heal, 2) === 0) {
          this.arp(t, [523, 784, 1047, 1568], 0.06, 0.2 * intensity);
          this.blip(t, 260, 520, 0.35, 0.12 * intensity, 'sine');
        } else {
          this.arp(t, [587, 880, 1175, 1760], 0.07, 0.19 * intensity);
          this.blip(t, 220, 660, 0.4, 0.11 * intensity, 'triangle');
        }
        break;
      case SfxId.SupplyDrop:
        // Something arcane whistling in from the gate.
        if (this.pickTake(SfxId.SupplyDrop, 2) === 0) {
          this.blip(t, 600, 1400, 0.45, 0.14 * intensity, 'sine');
          this.blip(t + 0.1, 900, 1800, 0.35, 0.1 * intensity, 'triangle');
        } else {
          this.blip(t, 500, 1600, 0.5, 0.13 * intensity, 'triangle');
          this.blip(t + 0.14, 1200, 2000, 0.3, 0.09 * intensity, 'sine');
          this.whoosh(t, 0.5, 0.06 * intensity, 600, 2600);
        }
        break;
      case SfxId.PlayerDeath:
        // Doom drop: the sub falls out, a minor arp tumbles after it.
        this.blip(t, 160, 24, 1.4, 0.6 * intensity, 'sine');
        this.burst(t, 0.9, 0.35 * intensity, 700, 6);
        this.arp(t + 0.15, [880, 698, 587, 466, 392], 0.13, 0.16 * intensity);
        this.blip(t + 0.8, 55, 22, 0.9, 0.3 * intensity, 'sawtooth');
        break;
      case SfxId.NewThreat:
        // Tritone sting with a hiss under it.
        this.blip(t, 220, 215, 0.35, 0.2 * intensity, 'sawtooth');
        this.blip(t + 0.12, 311, 305, 0.4, 0.2 * intensity, 'square');
        this.whoosh(t, 0.55, 0.08 * intensity, 300, 2400);
        break;
      case SfxId.WeaponExpired:
        this.blip(t, 640, 70, 0.45, 0.18 * intensity, 'square');
        this.blip(t + 0.05, 420, 60, 0.4, 0.12 * intensity, 'triangle');
        this.burst(t + 0.2, 0.2, 0.08 * intensity, 1400, 2);
        break;
      case SfxId.Alarm:
        // Two-tone klaxon, twice.
        for (let k = 0; k < 2; k++) {
          this.blip(t + k * 0.42, 560, 540, 0.18, 0.16 * intensity, 'square');
          this.blip(t + k * 0.42 + 0.2, 420, 405, 0.18, 0.16 * intensity, 'square');
        }
        break;
      case SfxId.LowPowerWarning:
        // Alert chime: urgent minor warning ping
        this.blip(t, 760, 520, 0.14, 0.26 * intensity, 'triangle');
        this.blip(t + 0.08, 880, 580, 0.16, 0.28 * intensity, 'square');
        this.burst(t, 0.07, 0.1 * intensity, 1200, 3);
        break;
      case SfxId.Heartbeat:
        // Sub-bass double thud ("lub-dub")
        this.blip(t, 78, 36, 0.14, 0.45 * intensity, 'sine');
        this.burst(t, 0.08, 0.15 * intensity, 180, 2);
        this.blip(t + 0.13, 68, 30, 0.16, 0.35 * intensity, 'sine');
        this.burst(t + 0.13, 0.08, 0.12 * intensity, 150, 2);
        break;
      case SfxId.MinimapZoom:
        this.whoosh(t, 0.3, 0.07 * intensity, 500, 3200);
        this.blip(t + 0.18, 1400, 1900, 0.09, 0.05 * intensity, 'sine');
        break;
      case SfxId.Curtain:
        // Long rising whoosh, then the bolts slide home.
        this.whoosh(t, 0.8, 0.14 * intensity, 200, 3600);
        this.blip(t + 0.72, 180, 90, 0.25, 0.22 * intensity, 'square');
        this.blip(t + 0.72, 60, 30, 0.45, 0.4 * intensity, 'sine');
        break;
      case SfxId.CrowdRoar:
        if (this.playSample(intensity >= 1.15 ? 'crowd_cheer_big' : 'crowd_cheer', Math.min(1.1, 0.55 * intensity), delaySec, 0.03)) {
          break;
        }
        this.crowdRoar(t, intensity);
        break;
      case SfxId.CrowdApplause:
        if (this.playSample('crowd_applause', Math.min(1.05, 0.5 * intensity), delaySec, 0.02)) break;
        this.applause(t, intensity);
        break;
      case SfxId.Drumroll:
        this.drumroll(t, intensity);
        break;
      case SfxId.OrchestraHit:
        this.orchestraHit(t, intensity);
        break;
      case SfxId.OnAir:
        // Sigil ignites: hum swells, two chime partials, a soft filament tick.
        this.blip(t, 58, 60, 0.7, 0.22 * intensity, 'sawtooth');
        this.blip(t + 0.05, 880, 890, 0.5, 0.07 * intensity, 'sine');
        this.blip(t + 0.14, 1320, 1330, 0.6, 0.06 * intensity, 'triangle');
        this.burst(t + 0.02, 0.03, 0.08 * intensity, 5000, 1);
        break;
      case SfxId.CommercialJingle:
        // Bouncy broadcast sponsor stinger: cheerful triad arpeggio and bright shimmer
        this.arp(t, [330, 440, 554, 659, 880], 0.075, 0.3 * intensity);
        this.blip(t + 0.38, 659, 988, 0.2, 0.22 * intensity, 'sine');
        this.blip(t + 0.38, 1318, 1320, 0.25, 0.18 * intensity, 'triangle');
        this.burst(t + 0.38, 0.12, 0.08 * intensity, 4400, 2);
        break;
      case SfxId.MenuMove:
        this.blip(t, 620, 880, 0.08, 0.06 * intensity, 'triangle');
        this.burst(t, 0.02, 0.04 * intensity, 4000, 1);
        break;
      case SfxId.MenuConfirm:
        // Rune locks: two rising chime partials over a low thud.
        this.blip(t, 440, 660, 0.22, 0.09 * intensity, 'sine');
        this.blip(t + 0.06, 880, 1320, 0.32, 0.07 * intensity, 'triangle');
        this.blip(t, 90, 45, 0.28, 0.2 * intensity, 'sine');
        break;
      case SfxId.RuneWipe:
        this.whoosh(t, 0.55, 0.1 * intensity, 240, 3000);
        this.blip(t + 0.3, 1760, 2400, 0.16, 0.04 * intensity, 'sine');
        this.blip(t + 0.42, 90, 45, 0.3, 0.2 * intensity, 'sine');
        break;
      case SfxId.BossArrive:
        this.bossArrive(t, intensity);
        break;
      case SfxId.BossTelegraph:
        this.bossTelegraph(t, intensity);
        break;
      case SfxId.BossCast:
        this.bossCast(t, intensity);
        break;
      case SfxId.BossHurt:
        if (this.playSample('boss_hit', intensity, delaySec, 0.045) || this.playSample('attack_hit', intensity * 0.95, delaySec, 0.05)) {
          this.bossHurt(t, intensity * 0.4);
          break;
        }
        this.bossHurt(t, intensity);
        break;
      case SfxId.BossHalf:
        // Cracked bell over a low growl: the fight just changed gear.
        this.blip(t, 440, 436, 0.9, 0.16 * intensity, 'triangle');
        this.blip(t, 466, 470, 0.9, 0.1 * intensity, 'sine');
        this.blip(t + 0.05, 72, 60, 1.1, 0.3 * intensity, 'sawtooth');
        this.burst(t, 0.06, 0.2 * intensity, 3200, 2);
        this.whoosh(t + 0.2, 0.7, 0.07 * intensity, 300, 2600);
        break;
      case SfxId.BossBlink:
        // Vacuum pop in, glassy shimmer out at the new spot.
        this.whoosh(t, 0.22, 0.1 * intensity, 2800, 200);
        this.blip(t + 0.2, 1400, 2600, 0.14, 0.07 * intensity, 'sine');
        this.blip(t + 0.22, 90, 45, 0.25, 0.22 * intensity, 'sine');
        this.burst(t + 0.2, 0.05, 0.1 * intensity, 4500, 1);
        break;
      case SfxId.BossDeath:
        this.bossDeath(t, intensity);
        break;
      case SfxId.BossIncoming:
        this.bossIncoming(t, intensity);
        break;
      case SfxId.BossArriveWarlord:
        this.arriveWarlord(t, intensity);
        break;
      case SfxId.BossArriveSkullKing:
        this.arriveSkullKing(t, intensity);
        break;
      case SfxId.BossArriveColossus:
        this.arriveColossus(t, intensity);
        break;
      case SfxId.BossArriveMatriarch:
        this.arriveMatriarch(t, intensity);
        break;
      case SfxId.BossArriveSentinel:
        this.arriveSentinel(t, intensity);
        break;
      case SfxId.BossTelegraphAimed:
        // Rising tone: something is about to be thrown at you.
        this.blip(t, 380, 1500, 0.5, 0.09 * intensity, 'triangle');
        this.blip(t + 0.04, 570, 2250, 0.45, 0.04 * intensity, 'sine');
        this.whoosh(t, 0.5, 0.05 * intensity, 500, 4200);
        break;
      case SfxId.BossTelegraphRadial:
        // Low pulses: the room is about to fill.
        for (let k = 0; k < 4; k++) this.blip(t + k * 0.15, 72, 60, 0.13, (0.22 + k * 0.03) * intensity, 'sine');
        this.blip(t, 36, 54, 0.65, 0.2 * intensity, 'sine');
        this.burst(t, 0.6, 0.08 * intensity, 240, 8);
        break;
      case SfxId.BossTelegraphOrb:
        // Glassy shimmer: orbs gathering.
        for (let k = 0; k < 7; k++) {
          const f = 1500 + Math.random() * 1900;
          this.blip(t + k * 0.06 + Math.random() * 0.03, f, f * 1.05, 0.16, 0.035 * intensity, 'sine');
        }
        this.blip(t, 880, 1320, 0.5, 0.05 * intensity, 'triangle');
        this.whoosh(t, 0.5, 0.04 * intensity, 1200, 6000);
        break;
      case SfxId.BossTelegraphBeam:
        // Sawtooth charge: a beam capacitor filling.
        this.blip(t, 110, 740, 1.0, 0.11 * intensity, 'sawtooth');
        this.blip(t, 112, 752, 1.0, 0.09 * intensity, 'sawtooth');
        this.blip(t + 0.3, 440, 1760, 0.7, 0.04 * intensity, 'square');
        this.whoosh(t, 1.0, 0.07 * intensity, 300, 5200);
        this.burst(t + 0.85, 0.15, 0.1 * intensity, 4000, 1);
        break;
      case SfxId.BossTelegraphHeavy:
        // Stone wind-up: the hammer comes up, plates grind.
        this.burst(t, 0.8, 0.22 * intensity, 320, 10);
        this.blip(t, 55, 95, 0.8, 0.28 * intensity, 'sine');
        for (let k = 0; k < 4; k++) this.burst(t + 0.1 + k * 0.17, 0.06, 0.12 * intensity, 900 + k * 200, 6);
        this.blip(t + 0.5, 180, 260, 0.3, 0.06 * intensity, 'sawtooth');
        break;
      case SfxId.BossTelegraphInhale:
        // Sharp inhale before a screech or a dive.
        this.whoosh(t, 0.6, 0.12 * intensity, 3200, 500);
        this.blip(t + 0.1, 1200, 2600, 0.5, 0.04 * intensity, 'sine');
        this.blip(t, 60, 80, 0.6, 0.14 * intensity, 'sine');
        break;
      case SfxId.BossTelegraphGurgle:
        // Something is coming up.
        for (let k = 0; k < 7; k++) {
          const f = 90 + Math.random() * 160;
          this.blip(t + k * 0.07 + Math.random() * 0.03, f, f * 1.6, 0.09, 0.09 * intensity, 'sine');
        }
        this.burst(t, 0.6, 0.12 * intensity, 260, 9);
        this.blip(t, 48, 70, 0.6, 0.2 * intensity, 'sine');
        break;
      case SfxId.BossHurtStone:
        if (this.playSample('boss_hit', intensity, delaySec, 0.045) || this.playSample('attack_hit', intensity * 0.95, delaySec, 0.05)) {
          this.hurtStone(t, intensity * 0.45);
          break;
        }
        this.hurtStone(t, intensity);
        break;
      case SfxId.BossHurtBone:
        if (this.playSample('boss_hit', intensity, delaySec, 0.045) || this.playSample('attack_hit', intensity * 0.95, delaySec, 0.05)) {
          this.hurtBone(t, intensity * 0.45);
          break;
        }
        this.hurtBone(t, intensity);
        break;
      case SfxId.BossHurtGoo:
        if (this.playSample('boss_hit', intensity, delaySec, 0.045) || this.playSample('attack_hit', intensity * 0.95, delaySec, 0.05)) {
          this.hurtGoo(t, intensity * 0.45);
          break;
        }
        this.hurtGoo(t, intensity);
        break;
      case SfxId.BossHurtLeather:
        if (this.playSample('boss_hit', intensity, delaySec, 0.045) || this.playSample('attack_hit', intensity * 0.95, delaySec, 0.05)) {
          this.hurtLeather(t, intensity * 0.45);
          break;
        }
        this.hurtLeather(t, intensity);
        break;
      case SfxId.BossStomp:
        this.stomp(t, intensity);
        break;
      case SfxId.BossWallHit:
        // Stone on stone: a smash, a square crack, rubble skittering back into the room.
        this.burst(t, 0.14, 0.55 * intensity, 2400, 2);
        this.blip(t, 170, 38, 0.5, 0.65 * intensity, 'square');
        this.blip(t, 60, 24, 0.7, 0.6 * intensity, 'sine');
        for (let k = 0; k < 7; k++) this.burst(t + 0.08 + Math.random() * 0.5, 0.04, 0.1 * intensity, 1200 + Math.random() * 1400, 5);
        break;
      case SfxId.BossScreech:
        // Piercing detuned sweep riding an air blast.
        this.blip(t, 2100, 3600, 0.45, 0.13 * intensity, 'sawtooth');
        this.blip(t, 2130, 3680, 0.45, 0.09 * intensity, 'square');
        this.blip(t + 0.05, 1050, 1800, 0.4, 0.05 * intensity, 'triangle');
        this.whoosh(t, 0.5, 0.16 * intensity, 600, 5600);
        this.blip(t, 70, 32, 0.4, 0.35 * intensity, 'sine');
        break;
      case SfxId.BossSplat:
        this.burst(t, 0.1, 0.22 * intensity, 800, 4);
        this.blip(t + 0.01, 320, 70, 0.14, 0.14 * intensity, 'triangle');
        this.blip(t + 0.05, 500 + Math.random() * 300, 1100, 0.08, 0.05 * intensity, 'sine');
        break;
      case SfxId.BossSummon:
        // Chant swell with a pop for every body that answers.
        this.blip(t, 110, 111, 0.7, 0.1 * intensity, 'sawtooth');
        this.blip(t, 164.8, 166, 0.7, 0.07 * intensity, 'sawtooth');
        this.whoosh(t, 0.7, 0.06 * intensity, 300, 3200);
        for (let k = 0; k < Math.max(1, Math.round(intensity * 3)); k++) {
          const at = t + 0.35 + k * 0.09;
          this.whoosh(at, 0.12, 0.08, 2600, 300);
          this.blip(at + 0.1, 90, 45, 0.2, 0.16, 'sine');
        }
        break;
      case SfxId.BossSplit:
        // Giant wet tear, then a spatter of small slaps.
        this.burst(t, 0.45, 0.5 * intensity, 700, 4);
        this.blip(t, 140, 40, 0.5, 0.4 * intensity, 'triangle');
        this.blip(t + 0.02, 400, 90, 0.3, 0.2 * intensity, 'sawtooth');
        for (let k = 0; k < 6; k++) this.burst(t + 0.2 + Math.random() * 0.4, 0.06, 0.12 * intensity, 900 + Math.random() * 600, 4);
        break;
      case SfxId.BossSkullLaunch:
        // Bone whistle: hollow tone dropping as it leaves.
        this.blip(t, 1500, 700, 0.14, 0.08 * intensity, 'triangle');
        this.burst(t, 0.04, 0.1 * intensity, 3200, 3);
        break;
      case SfxId.BossDeathStone:
        this.deathStone(t, intensity);
        break;
      case SfxId.BossDeathBone:
        this.deathBone(t, intensity);
        break;
      case SfxId.BossDeathGoo:
        this.deathGoo(t, intensity);
        break;
      case SfxId.BossDeathLeather:
        this.deathLeather(t, intensity);
        break;
      case SfxId.BossDrumFill:
        this.drumFill(t, intensity);
        break;
      case SfxId.BossDownHit:
        // One dry hit, then nothing: the silence beat is the point.
        this.blip(t, 110, 30, 0.5, 0.7 * intensity, 'sine');
        this.burst(t, 0.2, 0.4 * intensity, 1800, 1.5);
        this.burst(t, 0.6, 0.25 * intensity, 300, 6);
        break;
      case SfxId.DeathSting:
        this.deathSting(t, intensity);
        break;
      case SfxId.VictorySting:
        this.victorySting(t, intensity);
        break;
      case SfxId.Dash:
        this.whoosh(t, 0.16, 0.11 * intensity, 500, 2400);
        this.blip(t, 280, 520, 0.08, 0.08 * intensity, 'sine');
        break;
      default:
        break;
    }
  }

  /** Continuous lightning hum while the staff is firing. */
  setBeam(active: boolean, overcharge: boolean): void {
    const ctx = this.buses.context;
    if (active && !this.beamNode) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 92;
      const lfo = ctx.createOscillator();
      lfo.type = 'square';
      lfo.frequency.value = 38;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = 40;
      lfo.connect(lfoGain);
      lfoGain.connect(osc.frequency);
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.16, ctx.currentTime + 0.05);
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 1200;
      filter.Q.value = 0.8;
      osc.connect(filter);
      filter.connect(gain);
      gain.connect(this.voices);
      osc.start();
      lfo.start();
      this.beamNode = { osc, gain, lfo };
    }
    if (this.beamNode) {
      this.beamNode.osc.frequency.setTargetAtTime(overcharge ? 130 : 92, ctx.currentTime, 0.05);
      this.beamNode.gain.gain.setTargetAtTime(overcharge ? 0.24 : 0.16, ctx.currentTime, 0.05);
    }
    if (!active && this.beamNode) {
      const n = this.beamNode;
      this.beamNode = null;
      n.gain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.04);
      n.osc.stop(ctx.currentTime + 0.25);
      n.lfo.stop(ctx.currentTime + 0.25);
    }
  }

  private vary(base: number, pct = 0.08): number {
    return base * (1 + (Math.random() * 2 - 1) * pct);
  }

  private pickTake(id: SfxId, n: number): number {
    const last = this.lastTake[id];
    let take = Math.floor(Math.random() * n);
    if (n > 1 && take === last) take = (take + 1) % n;
    this.lastTake[id] = take;
    return take;
  }

  private shotWand(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.ShotWand, 3);
    const p = this.vary(1, 0.06);
    if (take === 0) {
      this.blip(t, 880 * p, 320 * p, 0.055, 0.07 * intensity, 'triangle');
    } else if (take === 1) {
      this.blip(t, 740 * p, 260 * p, 0.07, 0.065 * intensity, 'sine');
    } else {
      this.blip(t, 980 * p, 400 * p, 0.04, 0.06 * intensity, 'triangle');
      this.blip(t + 0.028, 700 * p, 220 * p, 0.04, 0.04 * intensity, 'sine');
    }
  }

  private hit(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.Hit, 3);
    // Body thud plus a short crack so chip damage reads even without the sample.
    this.blip(t, this.vary(90, 0.08), 36, 0.09, 0.2 * intensity, 'sine');
    if (take === 0) {
      this.burst(t, 0.045, 0.2 * intensity, this.vary(2200, 0.12), 2);
      this.blip(t, this.vary(280, 0.1), 90, 0.07, 0.12 * intensity, 'triangle');
    } else if (take === 1) {
      this.burst(t, 0.04, 0.18 * intensity, 1600, 3);
      this.blip(t, this.vary(190, 0.1), 70, 0.08, 0.14 * intensity, 'sine');
    } else {
      this.blip(t, this.vary(1100, 0.1), 320, 0.04, 0.1 * intensity, 'square');
      this.burst(t, 0.05, 0.16 * intensity, 2800, 1.5);
    }
  }

  private kill(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.Kill, 5);
    const p = this.vary(1, 0.07);
    if (take === 0) {
      // Goo.
      this.blip(t, 520 * p, 140 * p, 0.11, 0.18 * intensity, 'triangle');
      this.burst(t, 0.08, 0.14 * intensity, 1600, 2);
    } else if (take === 1) {
      // Pop.
      this.blip(t, 680 * p, 200 * p, 0.08, 0.16 * intensity, 'square');
      this.burst(t, 0.06, 0.12 * intensity, 2200, 1);
    } else if (take === 2) {
      // Crunch.
      this.blip(t, 400 * p, 90 * p, 0.14, 0.17 * intensity, 'sawtooth');
      this.burst(t, 0.1, 0.13 * intensity, 1100, 3);
    } else if (take === 3) {
      // Wet splat: noise-led, pitch dives.
      this.burst(t, 0.09, 0.16 * intensity, 900, 4);
      this.blip(t + 0.01, 300 * p, 60 * p, 0.12, 0.12 * intensity, 'triangle');
    } else {
      // Squeak-pop: tiny upward chirp cut off by a click.
      this.blip(t, 900 * p, 1400 * p, 0.05, 0.11 * intensity, 'square');
      this.burst(t + 0.04, 0.04, 0.14 * intensity, 2800, 1);
    }
  }

  /** Entrance sting: sub drop, then a rune shimmer and a detuned choir swell. */
  private bossArrive(t: number, intensity: number): void {
    this.blip(t, 48, 30, 1.8, 0.7 * intensity, 'sine');
    this.burst(t, 0.5, 0.35 * intensity, 180, 14);
    this.whoosh(t + 0.1, 1.2, 0.12 * intensity, 200, 3200);
    // Choir-ish swell: three detuned saws through the arp path, held.
    const root = 110;
    this.blip(t + 0.35, root, root * 1.01, 1.6, 0.11 * intensity, 'sawtooth');
    this.blip(t + 0.35, root * 1.5, root * 1.505, 1.6, 0.08 * intensity, 'sawtooth');
    this.blip(t + 0.35, root * 1.189, root * 1.2, 1.6, 0.07 * intensity, 'triangle');
    // Rune glints scattered across the swell.
    for (let k = 0; k < 6; k++) {
      const f = 1400 + Math.random() * 1400;
      this.blip(t + 0.4 + k * 0.17 + Math.random() * 0.05, f, f * 1.06, 0.14, 0.035 * intensity, 'sine');
    }
    // Lands with a stone thud.
    this.blip(t + 1.45, 70, 28, 0.6, 0.55 * intensity, 'sine');
    this.burst(t + 1.45, 0.35, 0.3 * intensity, 420, 8);
  }

  /** Charge-up: a rising shimmer that tells the player something is coming. */
  private bossTelegraph(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossTelegraph, 3);
    if (take === 0) {
      this.blip(t, 300, 1200, 0.55, 0.09 * intensity, 'sawtooth');
      this.whoosh(t, 0.55, 0.06 * intensity, 400, 3600);
    } else if (take === 1) {
      this.blip(t, 240, 960, 0.6, 0.08 * intensity, 'triangle');
      this.blip(t + 0.05, 360, 1440, 0.55, 0.05 * intensity, 'sine');
      this.whoosh(t, 0.6, 0.05 * intensity, 300, 3000);
    } else {
      // Ticking rune charge: stutter blips climbing.
      for (let k = 0; k < 6; k++) this.blip(t + k * 0.08, 500 + k * 120, 560 + k * 130, 0.06, 0.05 * intensity, 'square');
      this.whoosh(t, 0.5, 0.05 * intensity, 500, 3200);
    }
  }

  /** Release: an arcane thump with glassy partials spraying off it. */
  private bossCast(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossCast, 3);
    if (take === 0) {
      this.blip(t, 180, 60, 0.22, 0.3 * intensity, 'square');
      this.burst(t, 0.12, 0.2 * intensity, 2200, 2);
      this.blip(t + 0.01, 1600, 900, 0.12, 0.08 * intensity, 'sine');
    } else if (take === 1) {
      this.blip(t, 220, 70, 0.2, 0.28 * intensity, 'sawtooth');
      this.burst(t, 0.09, 0.18 * intensity, 3000, 1);
      this.blip(t + 0.02, 2100, 1200, 0.1, 0.07 * intensity, 'triangle');
    } else {
      this.blip(t, 140, 50, 0.26, 0.3 * intensity, 'sine');
      this.burst(t, 0.14, 0.18 * intensity, 1600, 3);
      this.blip(t + 0.01, 1200, 2400, 0.09, 0.06 * intensity, 'sine');
    }
  }

  /** Hurt: a body thud and a crack so the champion reads as meat, not a UI tick. */
  private bossHurt(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossHurt, 3);
    this.blip(t, 70, 28, 0.16, 0.28 * intensity, 'sine');
    this.burst(t, 0.08, 0.26 * intensity, 900, 4);
    if (take === 0) {
      this.burst(t, 0.05, 0.2 * intensity, 1800, 2);
      this.blip(t, 150, 70, 0.12, 0.16 * intensity, 'triangle');
    } else if (take === 1) {
      this.burst(t, 0.045, 0.18 * intensity, 2200, 2);
      this.blip(t + 0.01, 190, 80, 0.11, 0.14 * intensity, 'square');
    } else {
      this.blip(t, 110, 50, 0.14, 0.18 * intensity, 'sawtooth');
      this.burst(t + 0.015, 0.07, 0.16 * intensity, 1200, 3);
    }
  }

  /** Death crescendo: rising bursts stacking up, a held scream, then the final blast. */
  private bossDeath(t: number, intensity: number): void {
    // Rumble bed under the whole thing.
    this.burst(t, 2.4, 0.22 * intensity, 160, 16);
    this.blip(t, 60, 40, 2.4, 0.3 * intensity, 'sine');
    // Bursts accelerate toward the end.
    let at = t;
    let gap = 0.24;
    let k = 0;
    while (at < t + 2.0) {
      const f = 900 + k * 110;
      this.burst(at, 0.1, (0.12 + k * 0.012) * intensity, f, 3);
      this.blip(at, 140 + k * 8, 60, 0.18, 0.12 * intensity, 'square');
      at += gap;
      gap = Math.max(0.09, gap * 0.9);
      k++;
    }
    // Held scream: detuned saws sliding up.
    this.blip(t + 0.6, 220, 660, 1.5, 0.1 * intensity, 'sawtooth');
    this.blip(t + 0.6, 224, 672, 1.5, 0.1 * intensity, 'sawtooth');
    this.whoosh(t + 0.8, 1.4, 0.1 * intensity, 300, 4200);
    // Final blast.
    const end = t + 2.22;
    this.blip(end, 90, 24, 1.4, 0.8 * intensity, 'sine');
    this.burst(end, 0.9, 0.6 * intensity, 500, 8);
    this.burst(end, 0.25, 0.35 * intensity, 4000, 1);
    this.arp(end + 0.15, [523, 659, 784, 1047, 1319, 1568], 0.07, 0.16 * intensity);
  }

  private golemKill(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.GolemKill, 3);
    if (take === 0) {
      this.blip(t, 160, 40, 0.45, 0.5 * intensity, 'sawtooth');
      this.burst(t, 0.35, 0.4 * intensity, 500, 8);
    } else if (take === 1) {
      // Rockfall: three staggered crunches over the drop.
      this.blip(t, 130, 34, 0.5, 0.46 * intensity, 'square');
      for (let k = 0; k < 3; k++) this.burst(t + k * 0.07, 0.09, 0.2 * intensity, 700 - k * 120, 6);
    } else {
      // Crack then collapse.
      this.burst(t, 0.05, 0.3 * intensity, 2600, 3);
      this.blip(t + 0.04, 190, 36, 0.4, 0.48 * intensity, 'sawtooth');
      this.burst(t + 0.05, 0.4, 0.34 * intensity, 420, 9);
    }
  }

  private prize(t: number, intensity: number): void {
    // Longer major fanfares so a prize reads apart from a gem tick.
    const take = this.pickTake(SfxId.Prize, 3);
    if (take === 0) {
      this.arp(t, [523, 659, 784, 1047], 0.09, 0.25 * intensity);
    } else if (take === 1) {
      this.arp(t, [587, 740, 880, 1175, 1480], 0.08, 0.22 * intensity);
      this.blip(t, 147, 294, 0.5, 0.08 * intensity, 'sine');
    } else {
      this.arp(t, [659, 523, 784, 1047, 1319], 0.085, 0.22 * intensity);
      this.blip(t + 0.3, 1319, 1330, 0.3, 0.06 * intensity, 'triangle');
    }
  }

  private portalHit(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.PortalHit, 3);
    const p = this.vary(1, 0.08);
    // Soft glass tick, not a square-wave slap — portals get hit constantly.
    if (take === 0) {
      this.blip(t, 280 * p, 220 * p, 0.06, 0.035 * intensity, 'triangle');
    } else if (take === 1) {
      this.blip(t, 200 * p, 110 * p, 0.08, 0.04 * intensity, 'sine');
    } else {
      this.blip(t, 320 * p, 240 * p, 0.04, 0.028 * intensity, 'sine');
      this.blip(t + 0.03, 210 * p, 150 * p, 0.045, 0.022 * intensity, 'triangle');
    }
  }

  private playerHurt(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.PlayerHurt, 3);
    if (take === 0) {
      this.blip(t, this.vary(200, 0.08), 60, 0.25, 0.4 * intensity, 'square');
      this.burst(t, 0.15, 0.3 * intensity, 800, 4);
    } else if (take === 1) {
      this.blip(t, this.vary(140, 0.08), 40, 0.32, 0.42 * intensity, 'sawtooth');
      this.burst(t, 0.2, 0.28 * intensity, 500, 5);
    } else {
      this.blip(t, this.vary(280, 0.08), 90, 0.16, 0.36 * intensity, 'square');
      this.burst(t, 0.1, 0.26 * intensity, 1200, 2);
    }
  }

  private blip(t: number, f0: number, f1: number, dur: number, vol: number, type: OscillatorType): void {
    const ctx = this.buses.context;
    f0 *= this.pitchMul;
    f1 *= this.pitchMul;
    dur *= this.stretchMul;
    const osc = ctx.createOscillator();
    osc.type = type;
    osc.frequency.setValueAtTime(Math.max(20, f0), t);
    osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(g);
    g.connect(this.voices);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  private burst(t: number, dur: number, vol: number, cutoff: number, q: number): void {
    const ctx = this.buses.context;
    dur *= this.stretchMul;
    cutoff = Math.min(ctx.sampleRate * 0.45, cutoff * this.toneMul);
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.loopStart = Math.random() * 0.5;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(cutoff, t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(60, cutoff * 0.15), t + dur);
    filter.Q.value = q;
    const g = ctx.createGain();
    g.gain.setValueAtTime(vol, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.voices);
    src.start(t, src.loopStart);
    src.stop(t + dur + 0.02);
  }

  /** Noise with a rising bandpass sweep: air moving, not an impact. */
  private whoosh(t: number, dur: number, vol: number, f0: number, f1: number): void {
    const ctx = this.buses.context;
    dur *= this.stretchMul;
    f0 *= this.toneMul;
    f1 = Math.min(ctx.sampleRate * 0.45, f1 * this.toneMul);
    const src = ctx.createBufferSource();
    src.buffer = this.noise;
    src.loop = true;
    src.loopStart = Math.random() * 0.5;
    const filter = ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.setValueAtTime(Math.max(60, f0), t);
    filter.frequency.exponentialRampToValueAtTime(Math.max(80, f1), t + dur);
    filter.Q.value = 1.2;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + dur * 0.6);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.voices);
    src.start(t, src.loopStart);
    src.stop(t + dur + 0.02);
  }

  private arp(t: number, notes: readonly number[], step: number, vol: number): void {
    // Arps only get a gentle pitch spread so chords stay in tune with the music bed.
    const wide = this.pitchMul;
    this.pitchMul = 1 + (wide - 1) * 0.35;
    step *= this.stretchMul;
    for (let i = 0; i < notes.length; i++) {
      this.blip(t + i * step, notes[i], notes[i] * 1.01, step * 2.2, vol, i === notes.length - 1 ? 'triangle' : 'square');
    }
    this.pitchMul = wide;
  }

  /**
   * Stadium roar: two bands of noise that swell over half a second, hold, then
   * fall away, with a scatter of whoops riding on top. `intensity` above 1
   * stretches the hold (a 1.6 roar lasts about five seconds).
   */
  private crowdRoar(t: number, intensity: number): void {
    const ctx = this.buses.context;
    const hold = 1.2 + Math.max(0, intensity - 0.6) * 2.2;
    const total = 0.5 + hold + 1.8;
    const vol = Math.min(1, intensity) * 0.34;
    const layers: Array<[BiquadFilterType, number, number, number]> = [
      ['bandpass', 520, 0.55, 1],
      ['bandpass', 1400, 0.7, 0.45],
      ['lowpass', 260, 0.4, 0.6],
    ];
    for (const [type, freq, q, mul] of layers) {
      const src = ctx.createBufferSource();
      src.buffer = this.noise;
      src.loop = true;
      src.loopStart = Math.random() * 0.5;
      const f = ctx.createBiquadFilter();
      f.type = type;
      f.frequency.setValueAtTime(freq * 0.8, t);
      f.frequency.linearRampToValueAtTime(freq, t + 0.5);
      f.frequency.linearRampToValueAtTime(freq * 0.7, t + total);
      f.Q.value = q;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(vol * mul, t + 0.5);
      g.gain.setValueAtTime(vol * mul, t + 0.5 + hold);
      g.gain.exponentialRampToValueAtTime(0.0001, t + total);
      src.connect(f);
      f.connect(g);
      g.connect(this.voices);
      src.start(t, src.loopStart);
      src.stop(t + total + 0.05);
    }
    const whoops = 5 + Math.floor(intensity * 6);
    for (let i = 0; i < whoops; i++) {
      const at = t + 0.2 + Math.random() * (hold + 0.6);
      const f0 = 320 + Math.random() * 280;
      this.blip(at, f0, f0 * (1.3 + Math.random() * 0.4), 0.18 + Math.random() * 0.14, 0.035 * Math.min(1, intensity), Math.random() < 0.5 ? 'sine' : 'triangle');
    }
  }

  /** A few hundred hands: dense short clicks that thin out over ~2.4s. */
  private applause(t: number, intensity: number): void {
    const dur = 1.6 + intensity * 1.2;
    const count = Math.floor(90 + intensity * 70);
    for (let i = 0; i < count; i++) {
      const u = Math.random();
      const at = t + u * u * dur;
      const fade = 1 - (at - t) / dur;
      this.burst(at, 0.012 + Math.random() * 0.02, 0.05 * intensity * (0.4 + fade), 2400 + Math.random() * 2600, 0.8);
    }
    // Room tone under the claps.
    this.burst(t, dur, 0.06 * intensity, 900, 0.6);
  }

  /** Snare roll that tightens from 90ms to 35ms over ~1.7s, then a crash and a floor tom. */
  private drumroll(t: number, intensity: number): void {
    let at = t;
    let gap = 0.09;
    const end = t + 1.7;
    let i = 0;
    while (at < end) {
      const rise = (at - t) / 1.7;
      this.burst(at, 0.05, (0.08 + rise * 0.12) * intensity, 3400, 1.5);
      if (i % 2 === 0) this.blip(at, 190, 150, 0.06, 0.05 * intensity, 'triangle');
      at += gap;
      gap = Math.max(0.035, gap * 0.955);
      i++;
    }
    this.burst(end, 0.9, 0.3 * intensity, 6000, 0.7);
    this.blip(end, 140, 48, 0.6, 0.55 * intensity, 'sine');
  }

  /** Brass stab: detuned saw stack swept through a lowpass, sub boom, cymbal wash, a choir tail. */
  private orchestraHit(t: number, intensity: number): void {
    const ctx = this.buses.context;
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(0.5 * intensity, t + 0.012);
    env.gain.exponentialRampToValueAtTime(0.18 * intensity, t + 0.35);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 1.6);
    const f = ctx.createBiquadFilter();
    f.type = 'lowpass';
    f.frequency.setValueAtTime(4200, t);
    f.frequency.exponentialRampToValueAtTime(380, t + 1.4);
    f.Q.value = 1.1;
    f.connect(env);
    env.connect(this.voices);
    const notes = [110, 164.81, 220, 261.63, 329.63, 440];
    for (let i = 0; i < notes.length; i++) {
      for (let d = -1; d <= 1; d += 2) {
        const osc = ctx.createOscillator();
        osc.type = i % 2 === 0 ? 'sawtooth' : 'square';
        osc.frequency.value = notes[i];
        osc.detune.value = d * (5 + i * 2);
        osc.connect(f);
        osc.start(t);
        osc.stop(t + 1.7);
      }
    }
    this.blip(t, 60, 30, 1.1, 0.6 * intensity, 'sine');
    this.burst(t, 1.2, 0.28 * intensity, 7000, 0.6);
    this.blip(t + 0.02, 55, 27, 0.5, 0.35 * intensity, 'triangle');
  }

  /** Longer, darker slam for the elimination replay when the generated sting is missing. */
  private deathSting(t: number, intensity: number): void {
    this.orchestraHit(t, intensity * 0.85);
    this.blip(t, 38, 22, 2.4, 0.55 * intensity, 'sine');
    this.blip(t, 73, 46, 2.1, 0.22 * intensity, 'sawtooth');
    this.blip(t + 0.04, 196, 147, 1.8, 0.16 * intensity, 'triangle');
    this.blip(t + 0.08, 294, 220, 1.6, 0.1 * intensity, 'sine');
    this.burst(t, 1.6, 0.32 * intensity, 4200, 0.8);
    this.whoosh(t, 1.4, 0.1 * intensity, 180, 2800);
    this.blip(t + 1.15, 98, 82, 1.4, 0.18 * intensity, 'triangle');
    this.blip(t + 1.15, 49, 36, 1.6, 0.28 * intensity, 'sine');
  }

  /** Brighter slam for the championship replay when the generated sting is missing. */
  private victorySting(t: number, intensity: number): void {
    this.orchestraHit(t, intensity);
    this.blip(t, 49, 73, 2.2, 0.48 * intensity, 'sine');
    this.blip(t, 196, 294, 1.9, 0.16 * intensity, 'triangle');
    this.blip(t, 330, 392, 1.5, 0.1 * intensity, 'sine');
    this.burst(t, 1.4, 0.26 * intensity, 5200, 0.55);
    this.whoosh(t, 1.15, 0.1 * intensity, 220, 3400);
    this.blip(t + 0.85, 147, 196, 1.5, 0.18 * intensity, 'triangle');
    this.blip(t + 0.85, 73, 98, 1.7, 0.26 * intensity, 'sine');
  }

  // ------------------------------------------------------------ boss: build

  /** One second from the last portal to the boss: sub rises, a shaker tightens, glints climb, pre-hit. */
  private bossIncoming(t: number, intensity: number): void {
    this.blip(t, 28, 78, 1.02, 0.5 * intensity, 'sine');
    this.burst(t, 1.0, 0.26 * intensity, 150, 12);
    this.whoosh(t, 1.0, 0.13 * intensity, 200, 4800);
    let at = t;
    let gap = 0.12;
    while (at < t + 0.95) {
      this.burst(at, 0.025, 0.09 * intensity, 2600, 2);
      at += gap;
      gap = Math.max(0.035, gap * 0.86);
    }
    for (let k = 0; k < 6; k++) this.blip(t + 0.15 + k * 0.14, 800 + k * 260, 900 + k * 300, 0.12, 0.04 * intensity, 'sine');
    // Pre-hit right where the boss appears.
    this.blip(t + 0.97, 75, 26, 0.45, 0.5 * intensity, 'sine');
    this.burst(t + 0.97, 0.3, 0.3 * intensity, 400, 8);
  }

  // ---------------------------------------------------------- boss: arrives

  /** Warlord: sub-bass drop, stone grind, a war horn a fifth apart, then the fall whistle. */
  private arriveWarlord(t: number, intensity: number): void {
    this.blip(t, 130, 26, 1.6, 0.75 * intensity, 'sine');
    this.burst(t + 0.05, 1.2, 0.34 * intensity, 360, 14);
    for (let k = 0; k < 6; k++) this.burst(t + 0.1 + k * 0.16, 0.07, 0.14 * intensity, 700 + Math.random() * 500, 7);
    // War horn: two detuned saws on F2 and the fifth, bending up as they swell.
    this.blip(t + 0.25, 87, 92, 1.4, 0.13 * intensity, 'sawtooth');
    this.blip(t + 0.25, 87.5, 92.5, 1.4, 0.1 * intensity, 'sawtooth');
    this.blip(t + 0.3, 130.8, 138, 1.3, 0.08 * intensity, 'sawtooth');
    this.blip(t + 0.3, 43.6, 46, 1.3, 0.16 * intensity, 'square');
    // Falling body: a whistle dropping toward the landing.
    this.whoosh(t + 0.8, 0.75, 0.12 * intensity, 4000, 300);
  }

  /** Skull King: reversed cymbal, a detuned choir shriek, a spray of bone rattles, then the pop-in. */
  private arriveSkullKing(t: number, intensity: number): void {
    this.whoosh(t, 1.3, 0.17 * intensity, 700, 9000);
    this.blip(t, 40, 55, 1.4, 0.3 * intensity, 'sine');
    this.blip(t + 0.3, 440, 880, 1.15, 0.06 * intensity, 'sawtooth');
    this.blip(t + 0.3, 446, 892, 1.15, 0.05 * intensity, 'sawtooth');
    this.blip(t + 0.32, 660, 1320, 1.1, 0.04 * intensity, 'triangle');
    this.blip(t + 0.34, 523, 1047, 1.1, 0.04 * intensity, 'square');
    for (let k = 0; k < 16; k++) this.burst(t + 0.2 + Math.random() * 1.1, 0.02, 0.09 * intensity, 1800 + Math.random() * 1600, 4);
    // Vacuum pop as the skull condenses.
    this.whoosh(t + 1.35, 0.22, 0.14 * intensity, 3200, 200);
    this.blip(t + 1.5, 90, 45, 0.3, 0.28 * intensity, 'sine');
  }

  /** Colossus: wet suction pulling down, a bubbling low gurgle, a heave as the floor gives. */
  private arriveColossus(t: number, intensity: number): void {
    this.whoosh(t, 0.9, 0.15 * intensity, 2600, 160);
    this.burst(t, 1.6, 0.26 * intensity, 220, 10);
    this.blip(t + 0.5, 38, 72, 1.1, 0.45 * intensity, 'sine');
    for (let k = 0; k < 12; k++) {
      const at = t + 0.2 + Math.random() * 1.3;
      const f = 90 + Math.random() * 180;
      this.blip(at, f, f * 1.7, 0.1, 0.1 * intensity, 'sine');
    }
    for (let k = 0; k < 8; k++) {
      const at = t + 0.4 + Math.random() * 1.2;
      const f = 500 + Math.random() * 700;
      this.blip(at, f, f * 1.9, 0.07, 0.04 * intensity, 'sine');
    }
    this.burst(t + 1.2, 0.5, 0.3 * intensity, 600, 5);
  }

  /** Matriarch: screech sweep over a flutter of wing flaps and a sub swell. */
  private arriveMatriarch(t: number, intensity: number): void {
    this.blip(t, 44, 62, 1.5, 0.35 * intensity, 'sine');
    this.whoosh(t, 0.9, 0.09 * intensity, 900, 6000);
    this.blip(t + 0.2, 1700, 3400, 0.7, 0.1 * intensity, 'sawtooth');
    this.blip(t + 0.2, 1720, 3460, 0.7, 0.07 * intensity, 'square');
    this.blip(t + 0.25, 850, 1700, 0.6, 0.04 * intensity, 'triangle');
    for (let k = 0; k < 7; k++) {
      const at = t + 0.4 + k * 0.17;
      this.burst(at, 0.13, 0.22 * intensity, 520, 3);
      this.blip(at, 130, 60, 0.11, 0.1 * intensity, 'triangle');
    }
  }

  /** Sentinel: three stone cracks, a charging hum sweeping up, the eye igniting. */
  private arriveSentinel(t: number, intensity: number): void {
    for (let k = 0; k < 3; k++) {
      const at = t + k * 0.36;
      this.burst(at, 0.09, 0.4 * intensity, 2800 - k * 400, 3);
      this.blip(at, 420, 90, 0.3, 0.25 * intensity, 'square');
      this.burst(at + 0.05, 0.3, 0.18 * intensity, 500, 8);
    }
    this.blip(t + 0.2, 55, 110, 1.5, 0.2 * intensity, 'sawtooth');
    this.blip(t + 0.2, 110, 220, 1.5, 0.1 * intensity, 'square');
    this.whoosh(t + 0.3, 1.3, 0.1 * intensity, 150, 2800);
    this.blip(t + 1.5, 1200, 2400, 0.22, 0.08 * intensity, 'sine');
    this.blip(t + 1.52, 1800, 3600, 0.18, 0.05 * intensity, 'triangle');
  }

  // ------------------------------------------------------------- boss: hurt

  private hurtStone(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossHurtStone, 3);
    this.blip(t, 64, 26, 0.18, 0.3 * intensity, 'sine');
    if (take === 0) {
      this.burst(t, 0.06, 0.28 * intensity, 2000, 3);
      this.blip(t, 110, 48, 0.14, 0.2 * intensity, 'square');
    } else if (take === 1) {
      this.burst(t, 0.05, 0.24 * intensity, 2800, 2);
      this.blip(t + 0.01, 150, 55, 0.12, 0.18 * intensity, 'sawtooth');
      this.burst(t + 0.05, 0.06, 0.1 * intensity, 1100, 5);
    } else {
      this.blip(t, 180, 70, 0.11, 0.18 * intensity, 'square');
      this.burst(t, 0.08, 0.24 * intensity, 1400, 4);
    }
  }

  private hurtBone(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossHurtBone, 3);
    this.blip(t, 80, 32, 0.14, 0.24 * intensity, 'sine');
    if (take === 0) {
      this.blip(t, 580, 240, 0.07, 0.18 * intensity, 'triangle');
      this.burst(t, 0.04, 0.22 * intensity, 3200, 4);
    } else if (take === 1) {
      this.blip(t, 500, 220, 0.08, 0.16 * intensity, 'square');
      for (let k = 0; k < 3; k++) this.burst(t + 0.03 + k * 0.03, 0.025, 0.12 * intensity, 2600, 4);
    } else {
      this.blip(t, 740, 320, 0.06, 0.16 * intensity, 'triangle');
      this.blip(t + 0.04, 440, 200, 0.07, 0.12 * intensity, 'triangle');
      this.burst(t, 0.04, 0.18 * intensity, 3600, 3);
    }
  }

  private hurtGoo(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossHurtGoo, 3);
    this.blip(t, 72, 30, 0.16, 0.22 * intensity, 'sine');
    if (take === 0) {
      this.burst(t, 0.11, 0.24 * intensity, 800, 4);
      this.blip(t + 0.01, 260, 70, 0.14, 0.16 * intensity, 'sine');
    } else if (take === 1) {
      this.burst(t, 0.1, 0.22 * intensity, 650, 5);
      this.blip(t, 210, 55, 0.15, 0.14 * intensity, 'triangle');
      this.blip(t + 0.06, 480, 860, 0.07, 0.06 * intensity, 'sine');
    } else {
      this.blip(t, 320, 100, 0.12, 0.16 * intensity, 'triangle');
      this.burst(t + 0.01, 0.12, 0.2 * intensity, 1000, 3);
    }
  }

  private hurtLeather(t: number, intensity: number): void {
    const take = this.pickTake(SfxId.BossHurtLeather, 3);
    this.blip(t, 68, 28, 0.14, 0.22 * intensity, 'sine');
    if (take === 0) {
      this.burst(t, 0.05, 0.24 * intensity, 1500, 2);
      this.blip(t, 820, 420, 0.09, 0.12 * intensity, 'sawtooth');
    } else if (take === 1) {
      this.burst(t, 0.06, 0.22 * intensity, 1200, 2);
      this.blip(t + 0.01, 1100, 600, 0.08, 0.1 * intensity, 'square');
      this.burst(t + 0.06, 0.09, 0.12 * intensity, 480, 3);
    } else {
      this.blip(t, 640, 980, 0.07, 0.11 * intensity, 'sawtooth');
      this.burst(t, 0.06, 0.22 * intensity, 1700, 2);
    }
  }

  // ---------------------------------------------------------- boss: impacts

  /** Ground slam: sub thud, crack, then debris skittering for half a second. */
  private stomp(t: number, intensity: number): void {
    this.blip(t, 95, 22, 0.9, 0.9 * intensity, 'sine');
    this.burst(t, 0.6, 0.55 * intensity, 350, 8);
    this.burst(t, 0.06, 0.35 * intensity, 2600, 2);
    this.blip(t, 180, 40, 0.3, 0.3 * intensity, 'square');
    for (let k = 0; k < 6; k++) this.burst(t + 0.08 + Math.random() * 0.45, 0.04, 0.1 * intensity, 1200 + Math.random() * 1000, 5);
  }

  // ----------------------------------------------------------- boss: deaths

  /** Shared final blast + tail for every death crescendo. */
  private deathBlast(end: number, intensity: number, tail: 'bell' | 'choir' | 'gurgle' | 'swarm'): void {
    this.blip(end, 90, 24, 1.4, 0.85 * intensity, 'sine');
    this.burst(end, 0.9, 0.6 * intensity, 500, 8);
    this.burst(end, 0.25, 0.4 * intensity, 5000, 1);
    switch (tail) {
      case 'bell':
        this.blip(end + 0.1, 220, 218, 1.8, 0.09 * intensity, 'sine');
        this.blip(end + 0.1, 330, 327, 1.8, 0.06 * intensity, 'sine');
        this.blip(end + 0.1, 554, 550, 1.4, 0.04 * intensity, 'triangle');
        break;
      case 'choir':
        for (const f of [440, 523, 659, 880]) this.blip(end + 0.12, f, f * 0.995, 1.6, 0.045 * intensity, 'sine');
        this.blip(end + 0.12, 441, 439, 1.6, 0.03 * intensity, 'sawtooth');
        break;
      case 'gurgle':
        for (let k = 0; k < 8; k++) {
          const f = 90 + Math.random() * 180;
          this.blip(end + 0.15 + k * 0.1, f, f * 1.5, 0.1, 0.08 * intensity, 'sine');
        }
        break;
      case 'swarm':
        for (let k = 0; k < 14; k++) {
          const f = 1400 + Math.random() * 2000;
          this.blip(end + 0.1 + Math.random() * 0.9, f, f * 1.3, 0.05, 0.03 * intensity, 'square');
        }
        break;
      default:
        break;
    }
    this.arp(end + 0.25, [523, 659, 784, 1047, 1319, 1568], 0.07, 0.14 * intensity);
  }

  /** Stone body: rumble, accelerating rockfall, the war horn dying, blast, bell. */
  private deathStone(t: number, intensity: number): void {
    const end = t + 2.02;
    this.burst(t, 2.2, 0.24 * intensity, 150, 16);
    this.blip(t, 60, 38, 2.2, 0.3 * intensity, 'sine');
    let at = t;
    let gap = 0.26;
    let k = 0;
    while (at < end - 0.1) {
      this.burst(at, 0.09, (0.14 + k * 0.014) * intensity, 700 - k * 20, 7);
      this.blip(at, 150 - k * 3, 50, 0.16, 0.12 * intensity, 'square');
      at += gap;
      gap = Math.max(0.08, gap * 0.88);
      k++;
    }
    // The horn sags and gives out.
    this.blip(t + 0.3, 92, 52, 1.6, 0.12 * intensity, 'sawtooth');
    this.blip(t + 0.3, 138, 78, 1.6, 0.08 * intensity, 'sawtooth');
    this.whoosh(t + 0.8, 1.2, 0.09 * intensity, 300, 3800);
    this.deathBlast(end, intensity, 'bell');
    for (let j = 0; j < 8; j++) this.burst(end + 0.3 + Math.random() * 0.9, 0.06, 0.12 * intensity, 900 + Math.random() * 900, 6);
  }

  /** Bone body: rattle storm thickening, a shriek climbing, reversed cymbal, blast, choir. */
  private deathBone(t: number, intensity: number): void {
    const end = t + 2.02;
    this.burst(t, 2.1, 0.16 * intensity, 200, 12);
    this.blip(t, 55, 40, 2.1, 0.24 * intensity, 'sine');
    for (let k = 0; k < 40; k++) {
      const u = Math.random();
      this.burst(t + u * u * 1.9, 0.02, 0.1 * intensity, 1800 + Math.random() * 1800, 4);
    }
    this.blip(t + 0.3, 440, 1760, 1.6, 0.08 * intensity, 'sawtooth');
    this.blip(t + 0.3, 446, 1790, 1.6, 0.07 * intensity, 'sawtooth');
    this.whoosh(t + 0.6, 1.4, 0.14 * intensity, 600, 9000);
    this.deathBlast(end, intensity, 'choir');
  }

  /** Goo body: an inflating gurgle, wet slaps stacking, the pop, splat rain. */
  private deathGoo(t: number, intensity: number): void {
    const end = t + 2.02;
    this.blip(t, 55, 150, 1.95, 0.32 * intensity, 'sine');
    this.burst(t, 2.0, 0.2 * intensity, 300, 8);
    let at = t;
    let gap = 0.24;
    while (at < end - 0.1) {
      this.burst(at, 0.08, 0.16 * intensity, 800 + Math.random() * 400, 4);
      this.blip(at, 200 + Math.random() * 200, 80, 0.1, 0.1 * intensity, 'triangle');
      at += gap;
      gap = Math.max(0.07, gap * 0.87);
    }
    for (let k = 0; k < 14; k++) {
      const f = 400 + Math.random() * 900;
      this.blip(t + Math.random() * 1.9, f, f * 1.9, 0.06, 0.04 * intensity, 'sine');
    }
    // Pop: the rain of goo lands over the next second.
    this.burst(end, 0.5, 0.6 * intensity, 900, 3);
    this.deathBlast(end, intensity, 'gurgle');
    for (let j = 0; j < 9; j++) this.burst(end + 0.2 + Math.random() * 0.9, 0.07, 0.14 * intensity, 700 + Math.random() * 500, 4);
  }

  /** Leather body: flaps going frantic, screech climbing, blast, the swarm scattering. */
  private deathLeather(t: number, intensity: number): void {
    const end = t + 2.02;
    this.blip(t, 50, 42, 2.0, 0.24 * intensity, 'sine');
    this.burst(t, 2.0, 0.14 * intensity, 240, 10);
    let at = t;
    let gap = 0.2;
    while (at < end - 0.1) {
      this.burst(at, 0.1, 0.2 * intensity, 520, 3);
      this.blip(at, 130, 60, 0.09, 0.09 * intensity, 'triangle');
      at += gap;
      gap = Math.max(0.06, gap * 0.87);
    }
    this.blip(t + 0.4, 1500, 3800, 1.5, 0.09 * intensity, 'sawtooth');
    this.blip(t + 0.4, 1520, 3900, 1.5, 0.06 * intensity, 'square');
    this.whoosh(t + 0.6, 1.4, 0.1 * intensity, 800, 7000);
    this.deathBlast(end, intensity, 'swarm');
  }

  // ------------------------------------------------------------ boss: music

  /** One bar (~1.7 s) of kick / snare with a sixteenth roll into a crash: hands the bed to the boss track. */
  private drumFill(t: number, intensity: number): void {
    // Four beats spanning the rune build (Config.boss.champion.buildSeconds) so the
    // crash lands on the frame the boss appears.
    const beat = Config.boss.champion.buildSeconds / 4;
    for (let b = 0; b < 4; b++) {
      const at = t + b * beat;
      this.blip(at, 150, 42, 0.22, 0.5 * intensity, 'sine');
      if (b % 2 === 1) {
        this.burst(at, 0.09, 0.3 * intensity, 3400, 1.5);
        this.blip(at, 190, 150, 0.06, 0.08 * intensity, 'triangle');
      }
    }
    // Last beat: sixteenth snares building, then the crash.
    for (let s = 0; s < 4; s++) {
      const at = t + 3 * beat + s * (beat / 4);
      this.burst(at, 0.06, (0.18 + s * 0.05) * intensity, 3600, 1.5);
      this.blip(at, 150, 42, 0.12, 0.3 * intensity, 'sine');
    }
    const crash = t + 4 * beat;
    this.burst(crash, 1.1, 0.34 * intensity, 7000, 0.6);
    this.blip(crash, 140, 40, 0.6, 0.6 * intensity, 'sine');
  }

  private loadSample(id: string): Promise<AudioBuffer | null> {
    const cached = this.samples.get(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const inflight = this.sampleLoading.get(id);
    if (inflight) return inflight;
    const p = (async (): Promise<AudioBuffer | null> => {
      try {
        const res = await fetch(`${SFX_PATH}/${id}.mp3`);
        if (!res.ok) return null;
        const type = res.headers.get('content-type') ?? '';
        if (type.includes('text/html')) return null;
        const data = await res.arrayBuffer();
        if (data.byteLength < 4096) return null;
        return await this.buses.context.decodeAudioData(data);
      } catch {
        return null;
      }
    })();
    this.sampleLoading.set(id, p);
    p.then((buffer) => {
      this.samples.set(id, buffer);
      this.sampleLoading.delete(id);
    });
    return p;
  }
}
